import React, { useState, useRef } from 'react';
import { ArrowLeft, User as UserIcon, Mail, Phone, Building, MapPin, Lock, Upload, Check, Eye, EyeOff, Shield, Smartphone, LogOut } from 'lucide-react';
import type { ViewMode } from './TopBar';
type Props = {
  viewMode: ViewMode;
  email?: string;
  onBack: () => void;
  onLogout?: () => void;
};
const DEFAULT_AVATAR: Record<ViewMode, string> = {
  client: 'https://cdn.magicpatterns.com/uploads/nf5ZnnY1RSyLrbNcTptaVQ/125232908-a8708312-6fb9-4d66-9ab0-67e98ab1edfb.jpg',
  operator: 'https://cdn.magicpatterns.com/uploads/ov9xs5AWWQhM5K61dbweFz/07EB67B8-43C6-4108-82D7-4E8C2BB23818.png'
};
const DEFAULT_NAME: Record<ViewMode, string> = {
  client: 'Store Milano Duomo',
  operator: 'Marco Rossi'
};
export function Profile({
  viewMode,
  email,
  onBack,
  onLogout
}: Props) {
  const [avatar, setAvatar] = useState<string>(DEFAULT_AVATAR[viewMode]);
  const fileRef = useRef<HTMLInputElement | null>(null);
  // Personal info
  const [name, setName] = useState(DEFAULT_NAME[viewMode]);
  const [emailValue, setEmailValue] = useState(email || (viewMode === 'client' ? 'customer@aisac.io' : 'admin@aisac.io'));
  const [phone, setPhone] = useState(viewMode === 'client' ? '+39 02 8845 2210' : '+39 02 1234 5678');
  const [role, setRole] = useState(viewMode === 'client' ? 'Store manager' : 'Operations lead');
  const [company, setCompany] = useState(viewMode === 'client' ? 'Dan John' : 'Aisac S.p.A.');
  const [address, setAddress] = useState(viewMode === 'client' ? 'Galleria Vittorio Emanuele, 12 — Milano' : 'Via Roma 123, 20100 Milano (MI)');
  // Password
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  // Toast
  const [toast, setToast] = useState<string | null>(null);
  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 2400);
  };
  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const url = URL.createObjectURL(f);
    setAvatar(url);
    showToast('Immagine profilo aggiornata');
  };
  const handleSaveInfo = (e: React.FormEvent) => {
    e.preventDefault();
    showToast('Informazioni salvate');
  };
  const handleChangePwd = (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentPwd || !newPwd) return;
    if (newPwd !== confirmPwd) {
      showToast('Le password non coincidono');
      return;
    }
    setCurrentPwd('');
    setNewPwd('');
    setConfirmPwd('');
    showToast('Password aggiornata');
  };
  return <div className="space-y-6 max-w-4xl" data-id="element-3812">
      {/* Header */}
      <div className="flex items-center gap-3" data-id="element-3813">
        <button type="button" onClick={onBack} className="h-9 w-9 rounded-lg flex items-center justify-center bg-[#181818] border border-[#262626] text-[#9A9A9A] hover:text-[#FAFAFA] hover:border-[#333] transition" aria-label="Indietro" data-id="element-3814">
          <ArrowLeft className="h-4 w-4" data-id="element-3815" />
        </button>
        <div data-id="element-3816">
          <h1 className="text-2xl font-semibold text-[#FAFAFA] font-heading tracking-tight" data-id="element-3817">
            Profilo
          </h1>
        </div>
      </div>

      {/* Avatar + identity */}
      <section className="bg-[#181818] border border-[#262626] rounded-xl p-5 sm:p-6" data-id="element-3818">
        <div className="flex items-center gap-5 flex-wrap" data-id="element-3819">
          <div className="relative" data-id="element-3820">
            <div className="h-20 w-20 rounded-full overflow-hidden ring-1 ring-[#262626]" data-id="element-3821">
              <img src={avatar} alt={name} className="h-full w-full object-cover" data-id="element-3822" />
            </div>
          </div>
          <div className="flex-1 min-w-0" data-id="element-3823">
            <div className="text-base font-semibold text-[#FAFAFA] font-heading truncate" data-id="element-3824">
              {name}
            </div>
            <div className="text-xs text-[#9A9A9A] mt-0.5 truncate" data-id="element-3825">
              {emailValue}
            </div>
            <div className="text-[11px] text-[#6B6B6B] mt-0.5" data-id="element-3826">{role}</div>
          </div>
          <div className="flex items-center gap-2" data-id="element-3827">
            <input ref={fileRef} type="file" accept="image/*" onChange={handleAvatarChange} className="hidden" data-id="element-3828" />
            <button type="button" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#181818] border border-[#262626] text-[#FAFAFA] text-sm font-medium hover:border-[#333] transition" data-id="element-3829">
              <Upload className="h-3.5 w-3.5" data-id="element-3830" />
              Cambia immagine
            </button>
          </div>
        </div>
      </section>

      {/* Personal info */}
      <form onSubmit={handleSaveInfo} className="bg-[#181818] border border-[#262626] rounded-xl p-5 sm:p-6" data-id="element-3831">
        <div className="flex items-center gap-2 mb-5" data-id="element-3832">
          <UserIcon className="h-4 w-4 text-[#9A9A9A]" data-id="element-3833" />
          <h3 className="text-base font-semibold text-[#FAFAFA] font-heading" data-id="element-3834">
            Informazioni personali
          </h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3835">
          <Field label="Nome" data-id="element-3836">
            <input type="text" value={name} onChange={e => setName(e.target.value)} className="w-full px-3 py-2.5 rounded-lg bg-[#0A0A0A] border border-[#262626] text-sm text-[#FAFAFA] focus:outline-none focus:border-[#D4FF3A]/40" data-id="element-3837" />
          </Field>
          <Field label="Email" icon={Mail} data-id="element-3838">
            <input type="email" value={emailValue} onChange={e => setEmailValue(e.target.value)} className="w-full px-3 py-2.5 rounded-lg bg-[#0A0A0A] border border-[#262626] text-sm text-[#FAFAFA] font-mono focus:outline-none focus:border-[#D4FF3A]/40" data-id="element-3839" />
          </Field>
          <Field label="Telefono" icon={Phone} data-id="element-3840">
            <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} className="w-full px-3 py-2.5 rounded-lg bg-[#0A0A0A] border border-[#262626] text-sm text-[#FAFAFA] font-mono focus:outline-none focus:border-[#D4FF3A]/40" data-id="element-3841" />
          </Field>
          <Field label="Ruolo" data-id="element-3842">
            <input type="text" value={role} onChange={e => setRole(e.target.value)} className="w-full px-3 py-2.5 rounded-lg bg-[#0A0A0A] border border-[#262626] text-sm text-[#FAFAFA] focus:outline-none focus:border-[#D4FF3A]/40" data-id="element-3843" />
          </Field>
          <Field label={viewMode === 'client' ? 'Negozio' : 'Azienda'} icon={Building} data-id="element-3844">
            <input type="text" value={company} onChange={e => setCompany(e.target.value)} className="w-full px-3 py-2.5 rounded-lg bg-[#0A0A0A] border border-[#262626] text-sm text-[#FAFAFA] focus:outline-none focus:border-[#D4FF3A]/40" data-id="element-3845" />
          </Field>
          <Field label="Indirizzo" icon={MapPin} data-id="element-3846">
            <input type="text" value={address} onChange={e => setAddress(e.target.value)} className="w-full px-3 py-2.5 rounded-lg bg-[#0A0A0A] border border-[#262626] text-sm text-[#FAFAFA] focus:outline-none focus:border-[#D4FF3A]/40" data-id="element-3847" />
          </Field>
        </div>
        <div className="flex justify-end mt-5" data-id="element-3848">
          <button type="submit" className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#D4FF3A] text-[#0A0A0A] text-sm font-semibold hover:bg-[#c2ed28] transition" data-id="element-3849">
            <Check className="h-4 w-4" data-id="element-3850" />
            Salva modifiche
          </button>
        </div>
      </form>

      {/* Password */}
      <form onSubmit={handleChangePwd} className="bg-[#181818] border border-[#262626] rounded-xl p-5 sm:p-6" data-id="element-3851">
        <div className="flex items-center gap-2 mb-5" data-id="element-3852">
          <Lock className="h-4 w-4 text-[#9A9A9A]" data-id="element-3853" />
          <h3 className="text-base font-semibold text-[#FAFAFA] font-heading" data-id="element-3854">
            Cambia password
          </h3>
        </div>
        <div className="space-y-4 max-w-md" data-id="element-3855">
          <Field label="Password attuale" data-id="element-3856">
            <PasswordInput value={currentPwd} onChange={setCurrentPwd} show={showPwd} onToggle={() => setShowPwd(v => !v)} data-id="element-3857" />
          </Field>
          <Field label="Nuova password" data-id="element-3858">
            <PasswordInput value={newPwd} onChange={setNewPwd} show={showPwd} onToggle={() => setShowPwd(v => !v)} data-id="element-3859" />
          </Field>
          <Field label="Conferma nuova password" data-id="element-3860">
            <PasswordInput value={confirmPwd} onChange={setConfirmPwd} show={showPwd} onToggle={() => setShowPwd(v => !v)} data-id="element-3861" />
          </Field>
        </div>
        <div className="flex justify-end mt-5" data-id="element-3862">
          <button type="submit" className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#D4FF3A] text-[#0A0A0A] text-sm font-semibold hover:bg-[#c2ed28] transition" data-id="element-3863">
            <Check className="h-4 w-4" data-id="element-3864" />
            Aggiorna password
          </button>
        </div>
      </form>

      {/* Security */}
      <section className="bg-[#181818] border border-[#262626] rounded-xl p-5 sm:p-6" data-id="element-3865">
        <div className="flex items-center gap-2 mb-5" data-id="element-3866">
          <Shield className="h-4 w-4 text-[#9A9A9A]" data-id="element-3867" />
          <h3 className="text-base font-semibold text-[#FAFAFA] font-heading" data-id="element-3868">
            Sicurezza
          </h3>
        </div>
        <div className="space-y-3" data-id="element-3869">
          <SecurityRow icon={Smartphone} title="Autenticazione a due fattori" description="Aggiungi un livello di sicurezza con un codice via app o SMS." actionLabel="Attiva" data-id="element-3870" />
          <SecurityRow icon={LogOut} title="Disconnetti tutte le sessioni" description="Esci da tutti i dispositivi a cui hai effettuato l'accesso." actionLabel="Esci ovunque" onAction={onLogout} danger data-id="element-3871" />
        </div>
      </section>

      {/* Toast */}
      {toast && <div className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-3.5 py-2.5 rounded-lg bg-[#181818] border border-[#D4FF3A]/40 shadow-2xl" data-id="element-3872">
          <Check className="h-4 w-4 text-[#D4FF3A]" data-id="element-3873" />
          <span className="text-sm text-[#FAFAFA]" data-id="element-3874">{toast}</span>
        </div>}
    </div>;
}
function Field({
  label,
  icon: Icon,
  children
}: {
  label: string;
  icon?: React.ElementType;
  children: React.ReactNode;
}) {
  return <div data-id="element-3875">
      <label className="block text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium mb-1.5 flex items-center gap-1" data-id="element-3876">
        {Icon && <Icon className="h-3 w-3" data-id="element-3877" />}
        {label}
      </label>
      {children}
    </div>;
}
function PasswordInput({
  value,
  onChange,
  show,
  onToggle
}: {
  value: string;
  onChange: (v: string) => void;
  show: boolean;
  onToggle: () => void;
}) {
  return <div className="relative" data-id="element-3878">
      <input type={show ? 'text' : 'password'} value={value} onChange={e => onChange(e.target.value)} placeholder="••••••••" className="w-full pl-3 pr-10 py-2.5 rounded-lg bg-[#0A0A0A] border border-[#262626] text-sm text-[#FAFAFA] font-mono focus:outline-none focus:border-[#D4FF3A]/40" data-id="element-3879" />
      <button type="button" onClick={onToggle} aria-label={show ? 'Nascondi password' : 'Mostra password'} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-[#6B6B6B] hover:text-[#FAFAFA] transition" data-id="element-3880">
        {show ? <EyeOff className="h-4 w-4" data-id="element-3881" /> : <Eye className="h-4 w-4" data-id="element-3882" />}
      </button>
    </div>;
}
function SecurityRow({
  icon: Icon,
  title,
  description,
  actionLabel,
  onAction,
  danger
}: {
  icon: React.ElementType;
  title: string;
  description: string;
  actionLabel: string;
  onAction?: () => void;
  danger?: boolean;
}) {
  return <div className="flex items-start justify-between gap-3 p-3 rounded-lg bg-[#0A0A0A] border border-[#1F1F1F]" data-id="element-3883">
      <div className="flex items-start gap-3 min-w-0" data-id="element-3884">
        <div className="h-8 w-8 rounded-lg bg-[#181818] border border-[#262626] flex items-center justify-center shrink-0" data-id="element-3885">
          <Icon className="h-4 w-4 text-[#9A9A9A]" data-id="element-3886" />
        </div>
        <div className="min-w-0" data-id="element-3887">
          <div className="text-sm font-medium text-[#FAFAFA]" data-id="element-3888">{title}</div>
          <div className="text-xs text-[#9A9A9A] mt-0.5" data-id="element-3889">{description}</div>
        </div>
      </div>
      <button type="button" onClick={onAction} className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium transition border ${danger ? 'bg-transparent border-[#EF4444]/30 text-[#EF4444] hover:bg-[#EF4444]/10' : 'bg-[#181818] border-[#262626] text-[#FAFAFA] hover:border-[#333]'}`} data-id="element-3890">
        {actionLabel}
      </button>
    </div>;
}